import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../authenticated-base.component';
import {Backend, PERMISSION} from '@generated-backend-types';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ContentHeaderService} from '../../../services/content-header.service';
import {TitleService} from '../../../services/title.service';
import {TemplateModalService} from '../../../services/template-modal.service';
import {ActivatedRoute} from '@angular/router';
import {FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {takeUntil} from 'rxjs';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {FormGroupComponent} from '../../../components/form-inputs/form-group/form-group.component';
import {
  DateTimeSelectorComponent
} from '../../../components/form-inputs/date-time-selector/date-time-selector.component';
import {MultiSelectEnumComponent} from '../../../components/form-inputs/multi-select-enum/multi-select-enum.component';
import {TabSwitcherComponent} from '../../../components/tab-switcher/tab-switcher.component';
import {
  MultiSelectWithServiceListComponent
} from '../../../components/form-inputs/multi-select-with-service-list/multi-select-with-service-list.component';
import {EmailSendComponent} from '../../../components/email-send/email-send.component';
import {BsModalService, ModalModule} from 'ngx-bootstrap/modal';
import {ReadOnlyStaticComponent} from '../../../components/form-inputs/read-only-static/read-only-static.component';
import {PatientService} from '../../../services/api/patients/patient.service';
import {AddableBackofficeUserService} from '../../../services/api/patients/addable-backoffice-user.service';
import {AddableDoctorService} from '../../../services/api/patients/addable-doctor.service';
import {DropdownItemColorDirective} from '../../../directives/dropdown-item-color.directive';
import {DiagnosisService} from '../../../services/api/diagnosis.service';
import {InquiriesComponent} from './inquiries/inquiries.component';
import {FormSubmitService} from '../../../services/form-submit.service';
import Patient = Backend.App.Resources.Dashboard.Patient;
import PatientData = Backend.App.Data.Dashboard.PatientData;
import PatientStateEnum = Backend.App.Enums.PatientStateEnum;
import PatientLanguageEnum = Backend.App.Enums.PatientLanguageEnum;
import EmailTypeEnum = Backend.App.Enums.Administration.EmailTypeEnum;
import PatientOriginEnum = Backend.App.Enums.PatientOriginEnum;
import PatientGenderEnum = Backend.App.Enums.PatientGenderEnum;
import ReducedUser = Backend.App.Resources.Dashboard.Administration.ReducedUser;
import {Dir} from '@angular/cdk/bidi';

@Component({
  selector: 'app-authenticated-patients-edit',
  templateUrl: './edit.component.html',
  imports: [
    FormsModule,
    TranslatePipe,
    ReactiveFormsModule,
    FormGroupComponent,
    DateTimeSelectorComponent,
    MultiSelectEnumComponent,
    TabSwitcherComponent,
    EmailSendComponent,
    ModalModule,
    MultiSelectWithServiceListComponent,
    ReadOnlyStaticComponent,
    DropdownItemColorDirective,
    InquiriesComponent,
    Dir
  ],
  providers: [BsModalService, TemplateModalService],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditComponent extends AuthenticatedBaseComponent implements OnInit {
  public patient?: Patient;
  public form: StrictFormGroup<PatientData> = this.fb.group({
    state: this.fb.control(PatientStateEnum.NEW, [Validators.required]),
    gender: this.fb.control(undefined as unknown as PatientGenderEnum, [Validators.required]),
    first_name: this.fb.control('', [Validators.required]),
    last_name: this.fb.control('', [Validators.required]),
    birthdate: this.fb.control('', [Validators.required]),
    email: this.fb.control('', [Validators.required]),
    phone_number: this.fb.control('', [Validators.required]),
    requests_callback: this.fb.control(false, [Validators.required]),
    request_description: this.fb.control('', [Validators.required]),
    diagnosis: this.fb.control(''),
    diagnoses: this.fb.control([]),
    previous_therapy: this.fb.control(''),
    date_of_last_mri_or_ct: this.fb.control(''),
    origin: this.fb.control(undefined as unknown as PatientOriginEnum),
    other_origin: this.fb.control(''),
    requests_access: this.fb.control(false, [Validators.required]),
    language: this.fb.control(PatientLanguageEnum.GERMAN, [Validators.required]),
    backoffice_user_id: this.fb.control(''),
    doctor_ids: this.fb.control<string[]>([]),
  });

  @ViewChild('emailBuilderModal') public emailBuilderModalTemplate?: TemplateRef<string>;
  protected emailType?: EmailTypeEnum;
  protected readonly PERMISSION = PERMISSION;
  protected readonly PatientStateEnum = PatientStateEnum;
  protected readonly PatientLanguageEnum = PatientLanguageEnum;
  protected readonly EmailTypeEnum = EmailTypeEnum;
  protected readonly PatientOriginEnum = PatientOriginEnum;
  protected readonly PatientGenderEnum = PatientGenderEnum;
  protected addableBackofficeUsers?: ReducedUser[] = [];

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly fb: NonNullableFormBuilder,
    private readonly patientService: PatientService,
    private readonly toastrService: ToastrService,
    private readonly templateModalService: TemplateModalService,
    protected readonly diagnosisService: DiagnosisService,
    protected readonly addableBackOfficeUserService: AddableBackofficeUserService,
    protected readonly addableDoctorService: AddableDoctorService,
    private readonly formSubmitService: FormSubmitService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public get backOfficeUserColors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const addableBackofficeUser of this.addableBackofficeUsers ?? []) {
      result[addableBackofficeUser.name] = addableBackofficeUser.color;
    }

    return result;
  }

  public ngOnInit() {
    this.emailType = EmailTypeEnum.PATIENT_ACCEPTANCE;
    this.activatedRoute.data.pipe(takeUntil(this.onDestroy$)).subscribe(data => {
      const patient: Patient | undefined = data['patient'];

      if (patient) {
        this.patient = patient;

        this.addableBackOfficeUserService.getPage()
          .subscribe(res => this.addableBackofficeUsers = res.data);

        this.cdr.markForCheck();

        const patientData: PatientData = {
          ...patient,
          gender: patient.gender ?? undefined as unknown as PatientGenderEnum,
          doctor_ids: patient.doctors?.map(user => user.id) ?? [],
          backoffice_user_id: patient.backoffice_user?.id ?? '',
          diagnoses: patient.diagnoses?.map(diagnosis => diagnosis.id) ?? [],
        };

        this.form.patchValue(patientData);
        this.form.controls.diagnoses.setValue(patient.diagnoses?.map((diagnoses) => diagnoses.id) ?? [])
        this.cdr.markForCheck();
      }

      this.titleService.setTitle('authenticated.patients.edit.title');
    });
  }

  public save() {
    if (this.patient) {
      this.formSubmitService.submit<Patient, PatientData, PatientService>({
        form: this.form,
        service: this.patientService,
        modelId: this.patient.id,
        updatePermission: PERMISSION.PATIENT_UPDATE_ALL,
        updateSuccessMessage: this.translate.instant('authenticated.patients.edit.success'),
        updateErrorMessage: this.translate.instant('authenticated.patients.edit.error'),
      }).subscribe(patient => {
        if (!patient) return;

        this.form.markAsPristine();
        this.form.markAsUntouched();

        this.toastrService.success(this.translate.instant('authenticated.patients.edit.success'));
        this.patient = patient;

        this.cdr.markForCheck();
      });
    }
  }

  public sendEmail(type: EmailTypeEnum) {
    this.emailType = type;
    if (this.emailBuilderModalTemplate) {
      this.templateModalService.open(this.emailBuilderModalTemplate);
    }
  }

  public onCloseEmailModal() {
    this.templateModalService.close();
  }
}
